import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Subject, takeUntil } from 'rxjs';
import { MaintenanceService, MaintenanceInfo } from 'app/shared/services/maintenance.service';

@Component({
  selector: 'maintenance-banner',
  templateUrl: './maintenance-banner.component.html',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule
  ]
})
export class MaintenanceBannerComponent implements OnInit, OnDestroy {
  maintenanceInfo: MaintenanceInfo | null = null;
  showBanner = false;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(private _maintenanceService: MaintenanceService) {}

  ngOnInit(): void {
    // Get maintenance information
    this._maintenanceService.getMaintenanceInfo()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((info: MaintenanceInfo) => {
        this.maintenanceInfo = info;
        this.showBanner = info.isMaintenanceScheduled;
      });
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  /**
   * Format the maintenance date and time range for display
   */
  getFormattedDateTimeRange(): string {
    if (!this.maintenanceInfo?.maintenanceStartDate || !this.maintenanceInfo?.maintenanceEndDate || 
        !this.maintenanceInfo?.maintenanceStartTime || !this.maintenanceInfo?.maintenanceEndTime) {
      return '';
    }

    const startDate = new Date(this.maintenanceInfo.maintenanceStartDate + 'T' + this.maintenanceInfo.maintenanceStartTime);
    const endDate = new Date(this.maintenanceInfo.maintenanceEndDate + 'T' + this.maintenanceInfo.maintenanceEndTime);

    const dateOptions: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };

    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit'
    };

    const formattedStartDate = startDate.toLocaleDateString('en-US', dateOptions);
    const formattedEndDate = endDate.toLocaleDateString('en-US', dateOptions);
    const formattedStartTime = startDate.toLocaleTimeString('en-US', timeOptions);
    const formattedEndTime = endDate.toLocaleTimeString('en-US', timeOptions);

    // If same date, show date once with time range
    if (this.maintenanceInfo.maintenanceStartDate === this.maintenanceInfo.maintenanceEndDate) {
      return `${formattedStartDate} from ${formattedStartTime} to ${formattedEndTime}`;
    }

    // If different dates, show full date range
    return `${formattedStartDate} at ${formattedStartTime} to ${formattedEndDate} at ${formattedEndTime}`;
  }

  /**
   * Close the banner
   */
  closeBanner(): void {
    this.showBanner = false;
  }
} 